/**
 * Route + URL helpers for the canonical read-only share surface `/share/<id>`.
 *
 * This is the ONLY URL that "Copy link" / "Embed in Notion" ever hand out — the
 * editor URL (`/f/<id>`) is never copied. Kept pure (no DOM writes) so the URL
 * construction and id parsing unit-test without a browser.
 */

// Same id character class as the editor route (uuid-ish, url-safe). A malformed
// id (path traversal, spaces, empty) must NOT resolve — it renders the uniform
// 404, never a redirect or an existence leak.
const SHARE_PATH_RE = /^\/share\/([A-Za-z0-9_-]+)\/?$/;

/** The canonical read-only path for a cloud file. */
export const sharePath = (id: string) => `/share/${id}`;

/**
 * The absolute canonical share URL. Always the `/share/` URL — never the editor
 * URL. `origin` defaults to the current window origin.
 */
export const shareUrl = (
  id: string,
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
): string => `${origin.replace(/\/$/, "")}${sharePath(id)}`;

/**
 * Return the file id if the pathname (default: current location) is a
 * `/share/<id>` URL, otherwise null. Malformed ids return null.
 */
export const getShareFileIdFromPath = (
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "",
): string | null => {
  const match = pathname.match(SHARE_PATH_RE);
  return match ? match[1] : null;
};

/**
 * Owner detection: a public file's row is readable by anyone via RLS, so the
 * viewer compares the session user id to the file's `user_id`. Anonymous
 * viewers (no user id) and a missing file are never the owner.
 */
export const isFileOwner = (
  userId: string | null | undefined,
  file: { user_id: string } | null | undefined,
): boolean => !!userId && !!file && file.user_id === userId;

/**
 * Whether the current `/share/<id>` surface should render in chrome-less embed
 * mode. Two independent signals, either of which is sufficient:
 *
 *   1. iframe context — `window.self !== window.top` (the primary signal).
 *   2. an explicit `?embed=1` query param — an override/fallback for
 *      detection-hostile contexts (sandboxed previews, some embed proxies).
 *
 * Pure: the window refs and the query string are injectable so the branching
 * unit-tests without a real browser. Reading a cross-origin window ref can
 * throw; we treat that conservatively as "framed".
 */
export const isEmbedMode = (env?: {
  self?: unknown;
  top?: unknown;
  search?: string;
}): boolean => {
  const w = typeof window !== "undefined" ? window : undefined;
  const search = env?.search ?? (w ? w.location.search : "");
  if (new URLSearchParams(search).get("embed") === "1") {
    return true;
  }
  try {
    const selfRef = env && "self" in env ? env.self : w?.self;
    const topRef = env && "top" in env ? env.top : w?.top;
    return !!selfRef && selfRef !== topRef;
  } catch {
    return true;
  }
};
